import React, { useState } from 'react';
import { type Page, type BoardingHouseInfo } from '../types';
import Icon from './ui/Icon';

interface SidebarProps {
  currentPage: Page;
  setCurrentPage: (page: Page) => void;
  boardingHouseInfo: BoardingHouseInfo;
  onOpenSettings: () => void;
}

const NavItem: React.FC<{
  page: Page;
  label: string;
  icon: React.ReactNode;
  isCurrent: boolean;
  onClick: () => void;
}> = ({ page, label, icon, isCurrent, onClick }) => {
  const baseClasses = 'flex items-center px-4 py-3 my-1 font-medium transition-colors duration-200 transform rounded-lg';
  const activeClasses = 'bg-primary-500 text-white';
  const inactiveClasses = 'text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700';
  
  return (
    <a
      href="#"
      className={`${baseClasses} ${isCurrent ? activeClasses : inactiveClasses}`}
      onClick={(e) => {
        e.preventDefault();
        onClick();
      }}
    >
      {icon}
      <span className="mx-4">{label}</span>
    </a>
  );
};

const Sidebar: React.FC<SidebarProps> = ({ currentPage, setCurrentPage, boardingHouseInfo, onOpenSettings }) => {
  const [isOpen, setIsOpen] = useState(false);

  const navItems = [
    { page: 'dashboard' as Page, label: 'Tổng quan', icon: <Icon name="dashboard" className="w-5 h-5" /> },
    { page: 'rooms' as Page, label: 'Quản lý phòng', icon: <Icon name="rooms" className="w-5 h-5" /> },
    { page: 'tenants' as Page, label: 'Quản lý khách thuê', icon: <Icon name="tenants" className="w-5 h-5" /> },
    { page: 'statistics' as Page, label: 'Thống kê', icon: <Icon name="statistics" className="w-5 h-5" /> },
  ];

  const handleNavClick = (page: Page) => {
      setCurrentPage(page);
      setIsOpen(false);
  }
  
  const handleSettingsClick = () => {
    onOpenSettings();
    setIsOpen(false);
  }

  return (
    <>
     <button className="fixed z-30 top-4 left-4 sm:hidden p-2 rounded-md bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400" onClick={() => setIsOpen(!isOpen)}>
       {isOpen ? <Icon name="close" /> : <Icon name="menu" />}
     </button>
     
     {isOpen && (
        <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-10 sm:hidden" 
            onClick={() => setIsOpen(false)}
            aria-hidden="true"
        ></div>
     )}


      <aside className={`fixed sm:relative z-20 w-64 h-full px-4 py-8 overflow-y-auto bg-white border-r dark:bg-gray-900 dark:border-gray-700 transition-transform transform ${isOpen ? 'translate-x-0' : '-translate-x-full'} sm:translate-x-0 flex flex-col`}>
        <div className="text-center mt-8 sm:mt-0">
            <div className="flex items-center justify-center">
                <Icon name="logo" className="w-8 h-8 text-primary-500"/>
                <h1 className="ml-2 text-2xl font-bold text-gray-800 dark:text-white">StaySmart</h1>
            </div>
             <p className="mt-2 text-lg font-semibold text-primary-700 dark:text-primary-400">
                {boardingHouseInfo.name || "Tên Nhà Trọ"}
            </p>
             {!boardingHouseInfo.name && (
              <p className="text-xs text-gray-500 italic -mt-1">
                (Thay đổi tại Cài đặt)
              </p>
            )}
        </div>

        <div className="flex flex-col justify-between flex-1 mt-6">
          <nav className="flex-grow">
            {navItems.map((item) => (
              <NavItem
                key={item.page}
                page={item.page}
                label={item.label}
                icon={item.icon}
                isCurrent={currentPage === item.page}
                onClick={() => handleNavClick(item.page)}
              />
            ))}
             <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <NavItem
                    page='guide'
                    label='Hướng dẫn sử dụng'
                    icon={<Icon name="help" className="w-5 h-5" />}
                    isCurrent={currentPage === 'guide'}
                    onClick={() => handleNavClick('guide')}
                />
                <NavItem
                    page='backup'
                    label='Sao lưu & Khôi phục'
                    icon={<Icon name="backup" className="w-5 h-5" />}
                    isCurrent={currentPage === 'backup'}
                    onClick={() => handleNavClick('backup')}
                />
                 <NavItem
                    page='dashboard' // Dummy page prop, not used for navigation
                    label='Cài đặt'
                    icon={<Icon name="settings" className="w-5 h-5" />}
                    isCurrent={false} // Never active as it's a modal trigger
                    onClick={handleSettingsClick}
                />
            </div>
          </nav>
          
           <div className="mt-auto p-3 bg-gray-50 dark:bg-gray-800 rounded-lg text-sm text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700">
            <h3 className="font-semibold text-base text-gray-800 dark:text-white">Thông tin nhà trọ</h3>
            {boardingHouseInfo.name ? (
                <>
                    <p className="mt-2 font-medium text-gray-700 dark:text-gray-300">{boardingHouseInfo.name}</p>
                    <p className="mt-1">Địa chỉ: {boardingHouseInfo.address}</p>
                    {boardingHouseInfo.phone && boardingHouseInfo.phone.split('-').map((p, i) => (
                      <p key={i} className="mt-1">ĐT: {p.trim()}</p>
                    ))}
                </>
            ) : (
                <p className="mt-2 text-xs text-gray-500 italic">
                    (Thay đổi tại Cài đặt thiết lập nhà trọ)
                </p>
            )}
          </div>

        </div>
      </aside>
    </>
  );
};

export default Sidebar;
